<!-- Design/HMI canvas (Pilar F, F4.1). Free placement (absolute px) of
     vector shapes; pointer drag moves, SE handle resizes, click selects.
     Gestures emit rect ops on pointer-up. Shapes render by props.shape;
     in the editor the BASE style shows (live state evaluation is F4.2). -->
<template>
  <div class="canvas-host">
  <div ref="wrap" class="syn-wrap" :class="{ run: !interactive, inspect: !interactive && inspect, grabbing: panning, grab: spaceDown && interactive, fit }" @pointerdown.capture="onWrapDown" @wheel="onWheel">
    <component :is="'style'" v-if="keyframesCss">{{ keyframesCss }}</component>
    <div class="zoom-pad" :style="padStyle">
    <div ref="frameEl" class="frame" :class="{ 'drop-target': dropActive }" :style="frameStyle" @pointermove="onFrameMove" @pointerdown.self="onFrameDown" @dragenter.prevent="onDragEnter" @dragover.prevent="onDragOver" @dragleave="onDragLeave" @drop.prevent="onDrop">
      <!-- Aligned grid overlay (toggleable). -->
      <div v-if="interactive && showGrid" class="frame-grid"></div>
      <!-- Rulers (toggleable): ticks every 100px along top + left. -->
      <template v-if="interactive && rulers">
        <div class="ruler rh"><span v-for="t in rulerX" :key="'rx' + t" class="rt" :style="{ left: t + 'px' }">{{ t }}</span></div>
        <div class="ruler rv"><span v-for="t in rulerY" :key="'ry' + t" class="rt" :style="{ top: t + 'px' }">{{ t }}</span></div>
      </template>
      <!-- Marquee selection rectangle (D1). -->
      <div v-if="marquee" class="marquee" :style="marqueeStyle"></div>
      <!-- Comment pins (C2). -->
      <div v-for="cm in (comments ?? [])" :key="cm.id" class="cmt-pin" :class="{ done: cm.resolved }" :style="{ left: (cm.x ?? 0) + 'px', top: (cm.y ?? 0) + 'px' }" @pointerdown.stop @click.stop="emit('comment-open', cm.id)"><MessageCircle class="size-4" /></div>
      <!-- Alignment guides while dragging (D1.2). -->
      <div v-for="(gx, i) in guides.vx" :key="'v' + i" class="guide v" :style="{ left: gx + 'px' }"></div>
      <div v-for="(gy, i) in guides.hy" :key="'h' + i" class="guide h" :style="{ top: gy + 'px' }"></div>
      <!-- Remote collaborators' cursors (F5.3). -->
      <div v-for="p in (peers ?? [])" :key="p.id" class="peer" :style="{ left: (p.x ?? 0) + 'px', top: (p.y ?? 0) + 'px', color: p.color }">
        <svg width="14" height="18" viewBox="0 0 14 18"><path d="M1 1 L1 14 L4.5 10.5 L7 16 L9 15 L6.5 9.5 L11 9 Z" :fill="p.color" stroke="#fff" stroke-width="1" /></svg>
        <span class="peer-tag" :style="{ background: p.color }">{{ p.label || p.by || 'editor' }}</span>
      </div>
      <!-- Shapes host. In grid mode this is a REAL CSS grid (display:grid) and
           each shape is a grid-item placed by its cell; otherwise it's a plain
           absolute fill so free/flex shapes position exactly as before. The
           rulers/marquee/guides/comment-pins/peers stay OUTSIDE this host. -->
      <div ref="gridHostEl" class="shape-host" :style="gridHostStyle" @pointerdown.self="onFrameDown">
      <div
        v-for="n in renderShapes"
        :key="n.id"
        class="shape"
        :class="[{ selected: interactive && isSel(n.id), 'inspect-on': inspect && !interactive && inspectId === n.id, locked: !interactive, group: n.kind === 'group', 'reparent-into': reparentTargetId === n.id, 'grid-item': gridMode, 'grid-panel': panelShown(n) }, animClass(n)]"
        :style="shapeStyle(n)"
        :tabindex="!interactive && (hasPseudo(n) || equipmentOf(n)) ? 0 : undefined"
        :role="!interactive && equipmentOf(n) ? 'button' : undefined"
        :aria-label="equipmentOf(n) ? `${equipmentOf(n)?.label} · estat i senyals` : undefined"
        :aria-describedby="equipmentSelection?.elementId===n.id && equipmentKind==='contents' ? equipmentPopoverId : undefined"
        :aria-expanded="!interactive && equipmentOf(n) ? equipmentSelection?.elementId===n.id && equipmentKind==='control' : undefined"
        @keydown="onEquipmentKey($event,n)"
        @pointerdown.stop="onDown($event, n)"
        @dblclick.stop="onShapeDblclick($event, n)"
        @mouseenter="onEquipmentHover($event,n)"
        @mousemove="onEquipmentMove($event,n)"
        @mouseleave="hoverId === n.id && (hoverId = null); scheduleEquipmentLeave()"
        @focusin="onEquipmentFocus($event,n)"
        @focusout="focusId === n.id && (focusId = null); scheduleEquipmentLeave()"
      >
        <!-- Grid mode: each top-level node reads as a dashboard panel (card +
             title header). The header overlays nothing — content sits below it. -->
        <div v-if="panelShown(n)" class="grid-panel-head"><span class="gp-name" :style="panelNameStyle(n)">{{ panelTitle(n) }}</span><span v-if="panelType(n) && panelOf(n)?.showType !== false" class="gp-type">{{ panelType(n) }}</span></div>
        <div class="shape-body" :class="{ 'grid-body': n.kind === 'group' && isGridGroup(n) }" :style="bodyStyleFor(n)">
        <!-- Component instance: render baked children relative to the group box. -->
        <!-- Container render: a group (all children) OR a carousel widget with
             child slides (one at a time, rotating). -->
        <IndustrialSilo v-if="siloOf(n)" :config="siloOf(n)!" :effects="effects ?? {}" :selected="equipmentSelection?.elementId===n.id && equipmentKind==='control'" :style="equipmentStyle(n)" />
        <IndustrialMachine v-else-if="machineOf(n)" :config="machineOf(n)!" :effects="effects ?? {}" :selected="equipmentSelection?.elementId===n.id && equipmentKind==='control'" />
        <template v-else-if="n.kind === 'group' || carEnabled(n)">
          <div v-if="n.kind === 'group' && (P(n).fill || P(n).stroke)" class="grp-bg" :style="grpStyle(n)"></div>
          <div
            v-for="(c, ci) in (n.children ?? [])"
            v-show="!carEnabled(n) || ci === carActive(n)"
            :key="c.id"
            class="child"
            :class="{ 'child-sel': interactive && isSel(c.id), 'inspect-on': inspect && !interactive && inspectId === c.id }"
            :style="carEnabled(n) ? carSlideStyle(c) : childStyleIn(n, c)"
            @pointerdown="onChildDown($event, n, c)"
            @dblclick="onChildDblclick($event, c)"
          >
            <SynWidget v-if="isWidget(c)" :kind="kind(c)" :p="P(c)" :fill="fill(c)" :stroke="stroke(c)" :color="colorOf(c)" :value="eff(c).value" :series="eff(c).series" :table="eff(c).table" :live="!interactive" @row-click="(ctx) => emit('trigger', c.id, 'click', ctx)" />
            <SynControl v-else-if="isControl(c)" :kind="kind(c)" :p="P(c)" :color="colorOf(c)" :value="eff(c).value" :options="eff(c).options" :live="!interactive" @change="(v) => emit('control', c.id, v)" />
            <SynChart v-else-if="isChart(c)" :p="P(c)" :fill="fill(c)" :table="eff(c).table" :series="eff(c).series" :live="!interactive" />
            <!-- SynMap's `interactive` = "map usable" (pan/zoom/marker clicks) → Run mode; in edit mode it's inert so the node drags on the canvas. -->
            <SynMap v-else-if="isMap(c)" :p="P(c)" :fill="fill(c)" :interactive="!interactive" :live="!interactive" @marker="(m: MapMarker) => onMarker(c, m)" />
            <SynEmbed v-else-if="isEmbed(c)" :kind="kind(c)" :p="P(c)" :interactive="interactive" :data-base="assetBase" :data-token="assetToken" @set-var="(n2: string, v: unknown) => emit('plugin-set-var', n2, v)" @command="(id: string, v: unknown) => emit('plugin-command', id, v)" />
            <SynShape v-else :kind="kind(c)" :fill="fill(c)" :stroke="stroke(c)" :text="displayText(c)" :font-size="fontSize(c)" :paths="symPaths(c)" :view-box="viewBox(c)" :radius="radiusOf(c)" :gradient="gradientOf(c)" :text-style="textStyleOf(c)" :stroke-width="strokeWidthOf(c)" :src="imgSrc(c)" :unit="P(c).unit" :points="pts(c)" :closed="P(c).closed" :curve="P(c).curve" :text-color="textColorOf(c)" :stroke-style="strokeStyleOf(c)" :border-sides="borderSidesOf(c)" :object-fit="objectFitOf(c)" :auto-size="autoSizeOf(c)" />
          </div>
          <!-- Carousel slide navigator (edit mode): flip slides to edit each. Run auto-rotates. -->
          <div v-if="carEnabled(n) && interactive && (n.children?.length ?? 0) > 1" class="car-nav" @pointerdown.stop>
            <button type="button" @click="carAdvance(n, -1)">‹</button>
            <span>{{ carActive(n) + 1 }}/{{ n.children?.length ?? 0 }}</span>
            <button type="button" @click="carAdvance(n, 1)">›</button>
          </div>
        </template>
        <SynWidget v-else-if="isWidget(n)" :kind="kind(n)" :p="P(n)" :fill="fill(n)" :stroke="stroke(n)" :color="colorOf(n)" :value="eff(n).value" :series="eff(n).series" :table="eff(n).table" :live="!interactive" @row-click="(ctx) => emit('trigger', n.id, 'click', ctx)" />
        <SynControl v-else-if="isControl(n)" :kind="kind(n)" :p="P(n)" :color="colorOf(n)" :value="eff(n).value" :options="eff(n).options" :live="!interactive" @change="(v) => emit('control', n.id, v)" />
        <SynChart v-else-if="isChart(n)" :p="P(n)" :fill="fill(n)" :table="eff(n).table" :series="eff(n).series" :live="!interactive" />
        <SynMap v-else-if="isMap(n)" :p="P(n)" :fill="fill(n)" :interactive="!interactive" :live="!interactive" @marker="(m: MapMarker) => onMarker(n, m)" />
        <SynEmbed v-else-if="isEmbed(n)" :kind="kind(n)" :p="P(n)" :interactive="interactive" :data-base="assetBase" :data-token="assetToken" @set-var="(n2: string, v: unknown) => emit('plugin-set-var', n2, v)" @command="(id: string, v: unknown) => emit('plugin-command', id, v)" />
        <SynShape v-else :kind="kind(n)" :fill="fill(n)" :stroke="stroke(n)" :text="displayText(n)" :font-size="fontSize(n)" :paths="symPaths(n)" :view-box="viewBox(n)" :radius="radiusOf(n)" :gradient="gradientOf(n)" :text-style="textStyleOf(n)" :stroke-width="strokeWidthOf(n)" :src="imgSrc(n)" :unit="P(n).unit" :points="pts(n)" :closed="P(n).closed" :curve="P(n).curve" :text-color="textColorOf(n)" :stroke-style="strokeStyleOf(n)" :border-sides="borderSidesOf(n)" :object-fit="objectFitOf(n)" :auto-size="autoSizeOf(n)" />
        </div>
        <template v-if="interactive && isPrimarySelected(n)">
          <!-- Smart tag: quick access to the data-binding assistant for this node. -->
          <button v-if="dataBindable(n)" class="smart-tag" type="button" title="Configurar datos…" @pointerdown.stop @click.stop="emit('open-data', n.id)"><Database class="size-3" /><span>Datos</span></button>
          <!-- Live rotation angle (kept upright by counter-rotating). -->
          <div v-if="resize && resize.id === n.id && resize.dir === 'rotate'" class="rot-badge" :style="{ transform: 'translateX(-50%) rotate(' + (-(resize.cur.rotation ?? 0)) + 'deg)' }">{{ ((Math.round(resize.cur.rotation ?? 0) % 360) + 360) % 360 }}°</div>
          <!-- Path/polygon: editable vertex handles instead of resize box. -->
          <template v-if="kind(n) === 'path'">
            <span v-for="(p, i) in pts(n)" :key="'v' + i" class="vh" :style="{ left: (p.x / 1000 * r(n).w) + 'px', top: (p.y / 1000 * r(n).h) + 'px' }" @pointerdown.stop="onVertexDown($event, n, i)" @dblclick.stop="removeVertex(n, i)"></span>
          </template>
          <!-- Grid mode: only the SE handle, which changes the cell span. -->
          <template v-else-if="gridMode">
            <span class="h se" @pointerdown.stop="onHandle($event, n, 'se')"></span>
          </template>
          <template v-else>
            <template v-if="n.kind !== 'group' || !!layoutOf(n)">
              <span v-for="hd in HANDLES" :key="hd" class="h" :class="hd" @pointerdown.stop="onHandle($event, n, hd)"></span>
            </template>
            <span class="rot" title="Rotar (⇧ = 15°)" @pointerdown.stop="onHandle($event, n, 'rotate')"></span>
          </template>
        </template>
      </div>
      </div>
    </div>
    </div>
  </div>
  <EquipmentPopover :id="equipmentPopoverId" :selection="equipmentSelection" :kind="equipmentKind" :effects="effects ?? {}" :command-host="equipmentCommandHost" :contents-host="equipmentContentsHost" @close="closeEquipment" @enter="cancelEquipmentLeave" @leave="scheduleEquipmentLeave" />
  <div v-if="scaleSelf && interactive" class="zoombar">
    <button title="Alejar" @click="setZoom(zoomSelf - 0.1)">−</button>
    <button class="zlbl" title="Restablecer (100%)" @click="resetZoom">{{ Math.round(zoomSelf * 100) }}%</button>
    <button title="Acercar" @click="setZoom(zoomSelf + 0.1)">+</button>
    <button class="zfit" title="Ajustar a la ventana" @click="fitZoom">Ajustar</button>
  </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { MessageCircle, Database } from 'lucide-vue-next';
import type { DesignerNode, DesignerOp, RectPlacement } from './opmodel';
import { useCanvasHost, useCanvasTheme } from './canvas-host';
import SynShape, { type SymbolPath, type Gradient, type TextStyle } from './SynShape.vue';
import SynWidget, { type SeriesPoint } from './SynWidget.vue';
import IndustrialSilo from './equipment/IndustrialSilo.vue';
import IndustrialMachine from './equipment/IndustrialMachine.vue';
import EquipmentPopover from './equipment/EquipmentPopover.vue';
import {createHoverIntent} from './equipment/hover-intent';
import {routeLineState} from './equipment/route-line';
import {equipmentConfig,equipmentHoverEnabled,siloConfig,type MachineConfig,type AnyEquipmentGesture,type EquipmentContentsHost,type EquipmentCommandHost} from './equipment/model';
import SynControl from './SynControl.vue';
const host = useCanvasHost();
const SynEmbed = host.embed;
// Leaflet-backed; loaded on demand so the map lib only ships when a map is used.
const SynMap = host.map;
import type { MapMarker } from './SynMap.vue';
// ECharts-backed multi-series chart; lazy so echarts only ships when used.
const SynChart = host.chart;
import { layoutOf, computeLayout, gridSpecOf, type GridSpec, type PanelSpec } from './layout';
import type { GridPlacement } from './opmodel';
import { type AnimSpec, SIMPLE_KINDS, customKeyframes, animShorthand, animName } from './animCss';

/** Live effect for a shape (from design_render): style overrides from
 *  the active state, the resolved value, and the active animation spec. */
export interface DataTable { columns: Array<{ key: string; label: string }>; rows: Array<Array<number | string | boolean | null>>; ts?: number[]; error?: string }
export interface ShapeEffect {
  // Data-driven (from design_render) + the classic state overrides:
  fill?: string; stroke?: string; color?: string; text?: string; value?: number | null; series?: SeriesPoint[]; table?: DataTable; animSpec?: AnimSpec; opacity?: number; hidden?: boolean; variant?: string;
  // Full style overrides — a state can now change (almost) any visual prop, not
  // just fill/stroke. Renderer helpers fall back to base props when unset.
  strokeWidth?: number; strokeStyle?: string; borderSides?: { t?: number; r?: number; b?: number; l?: number };
  radius?: number; bg?: string; textColor?: string;
  fontSize?: number; fontWeight?: number; fontFamily?: string; italic?: boolean; underline?: boolean;
  align?: string; valign?: string; lineHeight?: number; letterSpacing?: number;
  gradient?: { type?: 'linear' | 'radial'; angle?: number; from?: string; to?: string } | null;
  shadow?: { x?: number; y?: number; blur?: number; color?: string } | null;
  fit?: string; // image object-fit
  autoSize?: boolean; // text: scale font to the element box
  options?: string[]; // select control: resolved label-value options
  repeat?: { var: string; values: string[] }; // repeat node: instance values
  // ADR 41 F3 — connector point freshness/route for the canvas badge.
  route?: 'stored' | 'live'; age_ms?: number; live_error?: string;
  quality?: string; last_value?: unknown; received_at_ms?: number | null;
}
/** Data-widget shapes route to SynWidget (vs SynShape) — they read live
 *  value/series from the effect instead of rendering a static vector. */
const WIDGET_SHAPES = ['kpi', 'gauge', 'progress', 'level', 'table', 'carousel', 'video', 'alarm'] as const;
/** Interactive input controls → SynControl. They WRITE a variable on change. */
const CONTROL_SHAPES = ['slider', 'toggle', 'input', 'select'];
export interface PeerCursor { id: string; color: string; x?: number; y?: number; by?: string; label?: string }

const props = withDefaults(
  defineProps<{ shapes: DesignerNode[]; selectedIds: string[]; frame?: RectPlacement; interactive?: boolean; effects?: Record<string, ShapeEffect>; peers?: PeerCursor[]; zoom?: number; followCursor?: { x: number; y: number } | null; comments?: Array<{ id: string; x?: number | null; y?: number | null; resolved?: boolean }>; commentMode?: boolean; snap?: boolean; showGrid?: boolean; rulers?: boolean; scaleSelf?: boolean; canvasBg?: string; fit?: boolean; fitMode?: 'contain' | 'cover' | 'stretch'; fitPad?: number; forced?: Record<string, string>; previewState?: { id: string; index: number } | null; rootLayout?: Record<string, unknown>; assetBase?: string; assetToken?: string; inspect?: boolean; inspectId?: string | null; equipmentContentsHost?: EquipmentContentsHost; equipmentCommandHost?: EquipmentCommandHost }>(),
  { interactive: true, selectedIds: () => [], zoom: 1, snap: true },
);
// Follow-mode (C3): scroll the canvas so the followed peer's cursor centres.
watch(() => props.followCursor, (c) => {
  if (!c || !wrap.value) return;
  wrap.value.scrollTo({ left: c.x * z() - wrap.value.clientWidth / 2 + 24, top: c.y * z() - wrap.value.clientHeight / 2 + 24, behavior: 'smooth' });
});
// Zoom: in the standalone editor (scaleSelf) the canvas owns the zoom and
// scales the frame itself; inside the scaled storyboard layer the parent
// scales and `zoom` is just the coefficient to undo for pointer math.
const zoomSelf = ref(1);
// Kiosk scale-to-fit (point 6): auto-zoom so the whole frame fits the
// viewport, recomputed on container resize. Overrides the manual zoom.
const fitZoomVal = ref(1);
// Per-axis fit scale. contain/cover use a uniform scale (SX===SY); stretch
// scales each axis independently to fill exactly (distorts aspect). Default
// (no fitMode) = contain, preserving the designer's behaviour.
const fitSX = ref(1);
const fitSY = ref(1);
function recomputeFit(): void {
  if (!wrap.value) return;
  const W = props.frame?.w ?? 1280, H = props.frame?.h ?? 720;
  const pad = props.fitPad ?? 16;
  const clamp = (v: number): number => Math.max(0.05, Math.min(4, v));
  const sx = clamp((wrap.value.clientWidth - pad) / W);
  const sy = clamp((wrap.value.clientHeight - pad) / H);
  const mode = props.fitMode ?? 'contain';
  if (mode === 'stretch') { fitSX.value = sx; fitSY.value = sy; }
  else { const s = mode === 'cover' ? Math.max(sx, sy) : Math.min(sx, sy); fitSX.value = s; fitSY.value = s; }
  fitZoomVal.value = fitSX.value; // pointer math (interactive only) uses uniform scale
}
const z = () => (props.fit ? fitZoomVal.value : props.scaleSelf ? zoomSelf.value : (props.zoom || 1));
function setZoom(v: number): void { zoomSelf.value = Math.min(4, Math.max(0.1, Math.round(v * 100) / 100)); }
function resetZoom(): void { zoomSelf.value = 1; }
function fitZoom(): void {
  if (!wrap.value) return;
  const W = props.frame?.w ?? 1280, H = props.frame?.h ?? 720;
  const zf = Math.min((wrap.value.clientWidth - 48) / W, (wrap.value.clientHeight - 48) / H);
  zoomSelf.value = Math.min(2, Math.max(0.1, Math.round(zf * 100) / 100));
}
const emit = defineEmits<{
  (e: 'select-set', ids: string[]): void;
  (e: 'op', op: DesignerOp): void;
  (e: 'ops', ops: DesignerOp[]): void;
  (e: 'cursor', pos: { x: number; y: number }): void;
  (e: 'trigger', nodeId: string, event: 'click' | 'dblclick' | 'markerClick', ctx?: Record<string, unknown>): void;
  (e: 'comment-at', pos: { x: number; y: number }): void;
  (e: 'comment-open', id: string): void;
  (e: 'palette-drop', data: string, pos: { x: number; y: number }): void;
  (e: 'inspect', nodeId: string): void;
  (e: 'control', nodeId: string, value: number | string | string[]): void;
  (e: 'plugin-set-var', name: string, value: unknown): void;
  (e: 'plugin-command', id: string, value: unknown): void;
  (e: 'open-data', nodeId: string): void;
}>();

// One overlay per canvas. Opening is synchronous and does not wait for a render RPC.
const equipmentPopoverId=useId();
const equipmentSelection=ref<AnyEquipmentGesture|null>(null);
const equipmentKind=ref<'control'|'contents'>('contents');
let suppressEquipmentFocus=false;
let equipmentLeave:ReturnType<typeof setTimeout>|undefined;
const equipmentOf=(n:DesignerNode)=>equipmentConfig(P(n).equipment);
const siloOf=(n:DesignerNode)=>siloConfig(P(n).equipment);
const machineOf=(n:DesignerNode):MachineConfig|null=>{const c=equipmentOf(n);return c&&c.type!=='silo'?c:null;};
function equipmentStyle(n:DesignerNode){const b=equipmentOf(n)!.body;return {position:'absolute' as const,left:b.x+'px',top:b.y+'px',width:b.width+'px',height:b.height+'px'};}
function cancelEquipmentLeave(){if(equipmentLeave)clearTimeout(equipmentLeave);equipmentLeave=undefined;}
function closeEquipment(){equipmentHover.cancel();cancelEquipmentLeave();equipmentSelection.value=null;suppressEquipmentFocus=true;queueMicrotask(()=>{suppressEquipmentFocus=false;});}
function scheduleEquipmentLeave(){equipmentHover.cancel();cancelEquipmentLeave();if(equipmentKind.value==='contents')equipmentLeave=setTimeout(closeEquipment,180);}
function openEquipment(e:MouseEvent|FocusEvent|KeyboardEvent,n:DesignerNode,kind:'control'|'contents',target=e.currentTarget as HTMLElement){
  const config=equipmentOf(n);if(!config||props.interactive||props.inspect||kind==='contents'&&!equipmentHoverEnabled(config))return false;
  equipmentHover.cancel();
  if(kind==='control'){e.preventDefault();target.focus({preventScroll:true});}
  cancelEquipmentLeave();if(kind==='contents'&&equipmentSelection.value&&equipmentKind.value==='control')return true;
  const rect=target.getBoundingClientRect();
  const pointer='clientX' in e && (e.clientX!==0||e.clientY!==0);
  equipmentKind.value=kind;equipmentSelection.value={type:config.type,elementId:n.id,config,source:pointer?'pointer':'keyboard',anchor:kind==='contents'?{x:pointer?(e as MouseEvent).clientX:rect.left,y:rect.top}:pointer?{x:(e as MouseEvent).clientX,y:(e as MouseEvent).clientY}:{x:rect.left,y:rect.bottom}};
  return true;
}
type HoverTarget={event:MouseEvent;node:DesignerNode;target:HTMLElement};
const equipmentHover=createHoverIntent<HoverTarget>(({event,node,target})=>{if(target.isConnected)openEquipment(event,node,'contents',target);});
function onEquipmentHover(e:MouseEvent,n:DesignerNode){
  if(!props.interactive)hoverId.value=n.id;
  const config=equipmentOf(n);if(!config||!equipmentHoverEnabled(config)||props.interactive||props.inspect||equipmentKind.value==='control'&&equipmentSelection.value)return;
  if(equipmentSelection.value?.elementId===n.id){cancelEquipmentLeave();return;}
  closeEquipment();equipmentHover.schedule({event:e,node:n,target:e.currentTarget as HTMLElement},{x:e.clientX,y:e.clientY});
}
function onEquipmentMove(e:MouseEvent,n:DesignerNode){equipmentHover.move({event:e,node:n,target:e.currentTarget as HTMLElement},{x:e.clientX,y:e.clientY});}
function onEquipmentFocus(e:FocusEvent,n:DesignerNode){if(suppressEquipmentFocus)return;if(!props.interactive)focusId.value=n.id;openEquipment(e,n,'contents');}
function onEquipmentKey(e:KeyboardEvent,n:DesignerNode){if((e.key==='Enter'||e.key===' ')&&equipmentOf(n)){e.preventDefault();openEquipment(e,n,'control');}}
watch(()=>props.shapes,closeEquipment);
onBeforeUnmount(()=>{equipmentHover.cancel();cancelEquipmentLeave();});

const selSet = computed(() => new Set(props.selectedIds));
const isSel = (id: string) => selSet.value.has(id);
const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as const;
type HandleDir = (typeof HANDLES)[number] | 'rotate';
function isPrimarySelected(n: DesignerNode): boolean {
  return props.selectedIds.length === 1 && props.selectedIds[0] === n.id;
}

const wrap = ref<HTMLElement | null>(null);
const frameEl = ref<HTMLElement | null>(null);
function onFrameMove(e: PointerEvent): void {
  if (!props.interactive || !frameEl.value) return;
  const r = frameEl.value.getBoundingClientRect();
  emit('cursor', { x: Math.round((e.clientX - r.left) / z()), y: Math.round((e.clientY - r.top) / z()) });
}
const ownScale = () => (props.fit ? fitZoomVal.value : zoomSelf.value);
const frameStyle = computed(() => {
  const w = props.frame?.w ?? 1280, h = props.frame?.h ?? 720;
  // Grid mode: dark canvas like the dashboard designer, so the gaps between
  // panel cards read as empty space (not white paper).
  const bg = props.canvasBg || (gridMode.value ? 'var(--color-surface-2)' : '#ffffff');
  const s: Record<string, string> = { width: w + 'px', height: h + 'px', background: bg };
  const sc = ownScale();
  if (props.fit) { s.transform = `scale(${fitSX.value}, ${fitSY.value})`; s.transformOrigin = '0 0'; }
  else if (props.scaleSelf && sc !== 1) { s.transform = `scale(${sc})`; s.transformOrigin = '0 0'; }
  return s;
});
// Scroll/centring space for the scaled frame (matches the zoomed size).
const padStyle = computed(() => props.fit
  ? { width: (props.frame?.w ?? 1280) * fitSX.value + 'px', height: (props.frame?.h ?? 720) * fitSY.value + 'px' }
  : props.scaleSelf
    ? { width: (props.frame?.w ?? 1280) * ownScale() + 'px', height: (props.frame?.h ?? 720) * ownScale() + 'px' }
    : {});
const rulerX = computed(() => { const w = props.frame?.w ?? 1280; const out: number[] = []; for (let t = 0; t <= w; t += 100) out.push(t); return out; });
const rulerY = computed(() => { const h = props.frame?.h ?? 720; const out: number[] = []; for (let t = 0; t <= h; t += 100) out.push(t); return out; });

const theme = useCanvasTheme();
const { t } = useI18n();
let fitRO: ResizeObserver | null = null;
onMounted(() => {
  void theme.load();
  if (props.scaleSelf) { window.addEventListener('keydown', onSpaceKey); window.addEventListener('keyup', onSpaceKey); }
  if (wrap.value && typeof ResizeObserver !== 'undefined') {
    fitRO = new ResizeObserver(() => { updateViewportW(); if (props.fit) recomputeFit(); });
    fitRO.observe(wrap.value);
  }
  updateViewportW();
  if (props.fit) recomputeFit();
  setupCarousels();
});
watch(() => props.fit, (f) => { if (f) recomputeFit(); });
watch(() => [props.fitMode, props.fitPad], () => { if (props.fit) recomputeFit(); });
watch(() => props.frame, () => { if (props.fit) recomputeFit(); }, { deep: true });

const P = (n: DesignerNode) => (n.props ?? {}) as Record<string, any>;
const fx = (n: DesignerNode): ShapeEffect | undefined => props.effects?.[n.id];
const kind = (n: DesignerNode) => P(n).shape ?? 'rect';
const isWidget = (n: DesignerNode) => (WIDGET_SHAPES as readonly string[]).includes(kind(n));
// A node worth a "configure data" smart tag: a data widget/chart/control, or
// any shape already carrying a binding (e.g. a rect whose fill is data-driven).
const dataBindable = (n: DesignerNode) =>
  isWidget(n) || isChart(n) || isControl(n) || !!(n as { dataBinding?: unknown }).dataBinding;
const isControl = (n: DesignerNode) => CONTROL_SHAPES.includes(kind(n));
const isChart = (n: DesignerNode) => kind(n) === 'chart';
const isMap = (n: DesignerNode) => kind(n) === 'map';
const isEmbed = (n: DesignerNode) => kind(n) === 'embed' || kind(n) === 'plugin';
// Marker click → fire the node's markerClick event with the row in scope.
// Run mode ONLY: the canvas's `interactive` means EDIT mode (the opposite of
// SynMap's prop of the same name), where actions never execute — the gate
// used to be inverted, leaving markers inert exactly where actions run.
function onMarker(n: DesignerNode, m: MapMarker): void {
  if (props.interactive) return;
  emit('trigger', n.id, 'markerClick', m.data ?? (m as unknown as Record<string, unknown>));
}
// Image source: a gallery asset (props.assetId → /api/assets/:id?token=) or a direct URL.
function imgSrc(n: DesignerNode): string | undefined {
  const aid = P(n).assetId as string | undefined;
  if (aid && props.assetToken) return `${props.assetBase ?? ''}/api/assets/${aid}?token=${encodeURIComponent(props.assetToken)}`;
  return P(n).src as string | undefined;
}
interface Vec { x: number; y: number }
// Editable path/polygon points (0..1000 space). While a vertex is dragging,
// return the working copy so the render + handles follow live.
function pts(n: DesignerNode): Vec[] {
  if (vtx.value && vtx.value.id === n.id) return vtx.value.points;
  return (P(n).points as Vec[] | undefined) ?? [];
}
const tok = (v: unknown) => theme.resolve(v) as string | undefined;

// hover/focus interaction states (run mode, P3): a state with trigger
// 'hover'/'focus' applies its overrides while the shape is hovered/focused.
const hoverId = ref<string | null>(null);
const focusId = ref<string | null>(null);
// Live viewport width (for breakpoint states), tracked by the ResizeObserver.
const viewportW = ref(1280);
function updateViewportW(): void { if (wrap.value) viewportW.value = wrap.value.clientWidth; }
/** A state's overrides as a ShapeEffect: copy every style key (a state may now
 *  override almost any prop), resolving token refs on the colour fields. */
function stateOverrides(st: Record<string, unknown>): Partial<ShapeEffect> {
  const { when: _w, trigger: _t, name: _n, maxw: _m, _k: _key, ...rest } = st as Record<string, unknown>;
  const o: Record<string, unknown> = { ...rest };
  for (const k of ['fill', 'stroke', 'bg', 'textColor'] as const) {
    if (o[k] != null) o[k] = tok(o[k]);
  }
  return o as Partial<ShapeEffect>;
}
const statesOf = (n: DesignerNode) => (n.states as Array<Record<string, unknown>> | undefined) ?? [];
function pseudoOverrides(n: DesignerNode): Partial<ShapeEffect> | null {
  if (props.interactive) return null;
  const which = focusId.value === n.id ? 'focus' : hoverId.value === n.id ? 'hover' : null;
  if (!which) return null;
  const st = statesOf(n).find((s) => (s.trigger ?? 'condition') === which);
  return st ? stateOverrides(st) : null;
}
/** Breakpoint state (P4 responsive): applies when the viewport width is ≤
 *  the state's maxw; the smallest matching breakpoint wins. Run mode only. */
function bpOverrides(n: DesignerNode): Partial<ShapeEffect> | null {
  if (props.interactive) return null;
  const bps = statesOf(n).filter((s) => s.trigger === 'breakpoint' && s.maxw != null);
  const match = bps.filter((s) => viewportW.value <= Number(s.maxw)).sort((a, b) => Number(a.maxw) - Number(b.maxw))[0];
  return match ? stateOverrides(match) : null;
}
/** Forced named state (goto_state action) — explicit override by name. */
function forcedOverrides(n: DesignerNode): Partial<ShapeEffect> | null {
  const name = props.forced?.[n.id];
  if (!name) return null;
  const st = statesOf(n).find((s) => s.name === name);
  return st ? stateOverrides(st) : null;
}
/** WYSIWYG preview: while a state is being edited in the inspector, show the
 *  selected node AS that state (by index) right on the edit canvas. */
function previewOverrides(n: DesignerNode): Partial<ShapeEffect> | null {
  const pv = props.previewState;
  if (!pv || pv.id !== n.id) return null;
  const st = statesOf(n)[pv.index];
  return st ? stateOverrides(st) : null;
}
/** Merged effect: server (data) base → breakpoint → forced → hover/focus →
 *  edited-state preview (highest, so the inspector shows what you're editing). */
function computeEff(n: DesignerNode): ShapeEffect {
  const e: ShapeEffect = { ...(fx(n) ?? {}) };
  for (const ov of [bpOverrides(n), forcedOverrides(n), pseudoOverrides(n), previewOverrides(n)]) {
    if (ov) for (const [k, v] of Object.entries(ov)) if (v !== undefined) (e as Record<string, unknown>)[k] = v;
  }
  return e;
}
// Memoized effects: every shape calls eff() ~10-15 times per render (fill,
// stroke, colorOf, fontSize, displayText, boxStyle...), each merging states[].
// Recompute ONCE per dependency change (effects/states/hover/focus/forced/
// preview/viewportW/theme) into a Map and let all the helpers read from it.
const effMap = computed<Map<string, ShapeEffect>>(() => {
  const m = new Map<string, ShapeEffect>();
  const walk = (list: DesignerNode[]): void => {
    for (const n of list) { m.set(n.id, computeEff(n)); if (n.children?.length) walk(n.children); }
  };
  walk(props.shapes);
  return m;
});
function eff(n: DesignerNode): ShapeEffect {
  return effMap.value.get(n.id) ?? computeEff(n);
}
/** ADR 41 F3 — small freshness/degradation badge for a connector point binding.
 *  Null for warehouse series (no route) so it only annotates live-routable
 *  points. `no_connection` → red; live → green + age; stored → muted age. */
function freshness(n: DesignerNode): { text: string; cls: string; title: string } | null {
  const e = eff(n);
  const fr = (k: string, p?: Record<string, unknown>) => t(`designer-ui.canvas.freshness.${k}`, p ?? {});
  if (e.live_error === 'no_connection') return { text: fr('noConnection'), cls: 'err', title: fr('noConnectionTitle') };
  if (e.live_error === 'bad' || e.live_error === 'stale') return {text:t('projectRuntime.'+e.live_error),cls:'err',title:t('projectRuntime.'+e.live_error)};
  if (e.live_error === 'not_collected') return { text: fr('notCollected'), cls: 'err', title: fr('notCollectedTitle') };
  if (!e.route) return null;
  const age = typeof e.age_ms === 'number' ? agoLabel(e.age_ms) : '';
  const withAge = (base: string) => (age ? `${base} · ${age}` : base);
  if (e.route === 'live') return { text: age ? `● ${age}` : `● ${fr('live')}`, cls: 'live', title: withAge(fr('liveTitle')) };
  return { text: age || fr('stored'), cls: 'stored', title: withAge(fr('storedTitle')) };
}
function agoLabel(ms: number): string {
  const s = Math.round(ms / 1000);
  const fr = (k: string, p?: Record<string, unknown>) => t(`designer-ui.canvas.freshness.${k}`, p ?? {});
  if (s < 1) return fr('now');
  if (s < 60) return fr('agoS', { n: s });
  if (s < 3600) return fr('agoM', { n: Math.round(s / 60) });
  return fr('agoH', { n: Math.round(s / 3600) });
}
function hasPseudo(n: DesignerNode): boolean {
  return !props.interactive && ((n.states as Array<Record<string, unknown>> | undefined) ?? []).some((s) => s.trigger === 'hover' || s.trigger === 'focus');
}
const fill = (n: DesignerNode) => eff(n).fill ?? tok(P(n).fill);
const routeAppearance = computed(()=>{
  const map=new Map<string,{stroke:string;width:number}>();
  const walk=(nodes:DesignerNode[])=>{for(const n of nodes){if(P(n).routeLine){const state=routeLineState(P(n).routeLine,props.effects??{});map.set(n.id,{stroke:state==='active'?'#008A63':state==='unknown'?'#A89471':'#8A979F',width:state==='active'?4:2.5});}if(n.children)walk(n.children);}};
  walk(props.shapes);return map;
});
const stroke = (n: DesignerNode) => routeAppearance.value.get(n.id)?.stroke ?? eff(n).stroke ?? tok(P(n).stroke);
// Gauge/level/progress fill colour: eff override (state/binding) → raw `color`.
const colorOf = (n: DesignerNode) => eff(n).color ?? tok(P(n).color as string | undefined);
const fontSize = (n: DesignerNode) => eff(n).fontSize ?? P(n).fontSize;
/** Format a live scalar for display: honour props.decimals, else round to 3
 *  (matching design_render's own fmtNum) so a float never prints 15 digits. */
function fmtLive(v: unknown, p: Record<string, unknown>): string {
  if (typeof v !== 'number' || !Number.isFinite(v)) return String(v);
  const d = p.decimals;
  return d != null && Number.isFinite(Number(d)) ? v.toFixed(Number(d)) : String(Math.round(v * 1000) / 1000);
}
function displayText(n: DesignerNode): string {
  const e = eff(n);
  if (e && e.value !== undefined && e.value !== null) {
    const p = P(n);
    const num = fmtLive(e.value, p);
    // Keep the author's template so the unit survives the live push: a shape
    // whose text is "{$value} MW" must not degrade to a bare number the moment
    // a value arrives. Tokens other than {$value} can only be resolved
    // server-side, so defer to design_render's interpolated text when present.
    const tpl = p.text;
    if (typeof tpl === 'string' && tpl.includes('{$value}')) {
      const rest = tpl.replace(/\{\$value\}/g, '');
      if (!rest.includes('{')) return tpl.replace(/\{\$value\}/g, num);
    }
    return e.text ?? num;
  }
  if (kind(n) === 'value') return P(n).placeholder ?? '0'; // edit-time sample
  return e?.text ?? P(n).text ?? n.name ?? 'texto';
}
const viewBox = (n: DesignerNode): string | undefined => P(n).viewBox as string | undefined;
function textStyleOf(n: DesignerNode): TextStyle | undefined {
  if (kind(n) !== 'text') return undefined;
  const p = P(n); const e = eff(n);
  return { fontFamily: e.fontFamily ?? p.fontFamily, fontWeight: e.fontWeight ?? p.fontWeight, italic: e.italic ?? p.italic, underline: e.underline ?? p.underline, align: e.align ?? p.align, valign: e.valign ?? p.valign, lineHeight: e.lineHeight ?? p.lineHeight, letterSpacing: e.letterSpacing ?? p.letterSpacing, writingMode: (p.writingMode as string | undefined), ellipsis: (p.ellipsis as boolean | undefined) };
}
/** Symbol paths with token refs in fill/stroke resolved to colours
 *  (keeps the 'currentFill'/'currentStroke' sentinels for SynShape). */
function symPaths(n: DesignerNode): SymbolPath[] | undefined {
  if (kind(n) !== 'symbol') return undefined;
  // Active variant (data-driven via state override, else the base prop).
  const variants = P(n).variants as Record<string, SymbolPath[]> | undefined;
  const active = eff(n).variant ?? (P(n).variant as string | undefined);
  const raw = (variants && active && variants[active]) ? variants[active]! : ((P(n).paths as SymbolPath[] | undefined) ?? []);
  const keep = (v?: string) => (v === 'currentFill' || v === 'currentStroke' || v === undefined ? v : (tok(v) as string));
  const route=routeAppearance.value.get(n.id);
  return raw.map((p) => ({ ...p, fill: keep(p.fill), stroke: route?'currentStroke':keep(p.stroke),...(route?{strokeWidth:route.width}:{}) }));
}
function animClass(n: DesignerNode): string {
  const k = eff(n).animSpec?.kind;
  return k && SIMPLE_KINDS.includes(k) ? 'anim-' + k : '';
}
/** Custom keyframe animations currently active → injected <style> + applied. */
const customAnims = computed(() => props.shapes
  .map((n) => ({ n, spec: eff(n).animSpec }))
  .filter((a): a is { n: DesignerNode; spec: AnimSpec } => a.spec?.kind === 'custom'));
const keyframesCss = computed(() => customAnims.value.map(({ n, spec }) => customKeyframes(animName(n.id), spec)).join('\n'));
function customAnimStyle(n: DesignerNode): Record<string, string | undefined> | null {
  const spec = eff(n).animSpec;
  if (spec?.kind !== 'custom') return null;
  const s = animShorthand(animName(n.id), spec);
  return { animation: s.animation, offsetPath: s.offsetPath, offsetRotate: spec.mode === 'path' ? 'auto' : undefined };
}
type RadiusVal = number | number[] | { tl?: number; tr?: number; br?: number; bl?: number };
const radiusOf = (n: DesignerNode): RadiusVal | undefined => (eff(n).radius ?? P(n).radius) as RadiusVal | undefined;
// border-radius CSS from a uniform number OR per-corner [tl,tr,br,bl]/{…}, with a px fallback.
function radiusCss(r: RadiusVal | undefined, fb: number): string {
  if (r == null) return fb + 'px';
  if (Array.isArray(r)) return r.map((x) => (Number(x) || 0) + 'px').join(' ');
  if (typeof r === 'object') return [r.tl, r.tr, r.br, r.bl].map((x) => (Number(x) || 0) + 'px').join(' ');
  return (Number(r) || 0) + 'px';
}
function gradientOf(n: DesignerNode): Gradient | null {
  const g = (eff(n).gradient ?? P(n).gradient) as { type?: 'linear' | 'radial'; angle?: number; from?: string; to?: string } | undefined | null;
  if (!g || !g.from || !g.to) return null;
  return { type: g.type ?? 'linear', angle: g.angle ?? 180, from: tok(g.from) ?? g.from, to: tok(g.to) ?? g.to };
}
function shadowFilter(n: DesignerNode): string | undefined {
  const s = (eff(n).shadow ?? P(n).shadow) as { x?: number; y?: number; blur?: number; color?: string } | undefined | null;
  if (!s) return undefined;
  return `drop-shadow(${s.x ?? 0}px ${s.y ?? 2}px ${s.blur ?? 4}px ${tok(s.color) ?? s.color ?? 'rgba(0,0,0,.35)'})`;
}
// Box/stroke props that a state may override too (fall back to base props).
const strokeWidthOf = (n: DesignerNode) => routeAppearance.value.get(n.id)?.width ?? eff(n).strokeWidth ?? P(n).strokeWidth;
const strokeStyleOf = (n: DesignerNode) => eff(n).strokeStyle ?? P(n).strokeStyle;
const borderSidesOf = (n: DesignerNode) => eff(n).borderSides ?? P(n).borderSides;
const textColorOf = (n: DesignerNode) => eff(n).textColor ?? P(n).textColor;
const objectFitOf = (n: DesignerNode) => eff(n).fit ?? P(n).fit;
const autoSizeOf = (n: DesignerNode) => eff(n).autoSize ?? P(n).autoSize;

// ── Content carousel ──────────────────────────────────────────────────
// The carousel widget (shape='carousel') becomes a rotating CONTAINER once it
// has child slides: it shows ONE child at a time (each fills the box) and
// auto-advances in run mode — a kiosk slideshow of arbitrary widgets (KPIs /
// charts / tables). With no children it falls back to the image carousel
// (SynWidget). Index + timers live here so it works the same in the editor's
// Run mode and the inline chat viewer; drop elements onto it to add slides.
const carIdx = reactive<Record<string, number>>({});
const isCarousel = (n: DesignerNode) => P(n).shape === 'carousel';
const carEnabled = (n: DesignerNode) => isCarousel(n) && (n.children?.length ?? 0) > 0;
// Clamp to the current slide count so a stale index (e.g. after deleting the
// visible slide) never points past the end and renders blank.
const carActive = (n: DesignerNode): number => {
  const len = n.children?.length ?? 0;
  return len ? Math.min(carIdx[n.id] ?? 0, len - 1) : 0;
};
function carAdvance(n: DesignerNode, delta: number): void {
  const len = n.children?.length ?? 0;
  if (len < 1) return;
  carIdx[n.id] = ((carActive(n) + delta) % len + len) % len;
}
/** A carousel slide fills the whole carousel box (its own rect is ignored). */
function carSlideStyle(c: DesignerNode): Record<string, string | undefined> {
  return { left: '0', top: '0', width: '100%', height: '100%', ...boxStyle(c) };
}
// Auto-rotate: one timer per carousel, only in run mode.
let carTimers: Record<string, ReturnType<typeof setInterval>> = {};
function setupCarousels(): void {
  for (const id in carTimers) clearInterval(carTimers[id]!);
  carTimers = {};
  if (props.interactive) return; // edit mode: manual nav only
  for (const n of props.shapes) {
    if (!carEnabled(n) || (n.children?.length ?? 0) < 2) continue;
    const ms = Math.max(800, Number(P(n).interval ?? 4000));
    const id = n.id;
    carTimers[id] = setInterval(() => {
      // Re-resolve the node on every tick: if the doc was replaced (load /
      // restore / remote ops) with the same signature, a closure over the OLD
      // node object would keep rotating stale children.
      const node = props.shapes.find((s) => s.id === id);
      const len = node?.children?.length ?? 0;
      if (!node || len < 1) return;
      const cur = carActive(node);
      if (P(node).loop === false && cur >= len - 1) return; // stop at the last slide
      carIdx[id] = (cur + 1) % len;
    }, ms);
  }
}
// Re-arm when the mode flips or the set of carousels/intervals/slides changes.
const carouselSig = computed(() => props.shapes.map((n) => `${n.id}:${isCarousel(n) ? (P(n).interval ?? 4000) : ''}:${n.children?.length ?? 0}`).join('|'));
watch([() => props.interactive, carouselSig], setupCarousels);
/** True when a group is a nested grid container (one level of CSS grid). */
function isGridGroup(n: DesignerNode): boolean { return layoutOf(n)?.mode === 'grid'; }
/** Body wrapper style: a grid group turns its body into a REAL CSS grid so its
 *  children become grid-items (placed by their `c.grid` cell). Otherwise the
 *  default absolute fill (handled in CSS). */
function bodyStyleFor(n: DesignerNode): Record<string, string> | undefined {
  if (!isGridGroup(n)) return undefined;
  const s = gridSpecOf(layoutOf(n));
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${s.cols}, 1fr)`,
    gridAutoRows: `${s.rowH}px`,
    columnGap: `${s.colGap}px`,
    rowGap: `${s.rowGap}px`,
  };
}
/** A group child is placed relative to the group box. When the group is an
 *  auto-layout container, its position/size come from the layout engine; when
 *  it's a grid container, the child is a grid-item placed by its cell. */
function childStyleIn(parent: DesignerNode, c: DesignerNode): Record<string, string | number | undefined> {
  const effect = eff(c);
  const visibility = {
    display: effect.hidden || (props.interactive && P(c).hidden === true) ? 'none' : undefined,
    opacity: effect.opacity !== undefined ? String(effect.opacity) : P(c).opacity != null ? String(P(c).opacity) : undefined,
  };
  if (isGridGroup(parent)) {
    const g = gridCellOf(c);
    return { gridColumn: `${g.x + 1} / span ${g.w}`, gridRow: `${g.y + 1} / span ${g.h}`, ...boxStyle(c), ...visibility };
  }
  let r = c.rect ?? { x: 0, y: 0, w: 40, h: 40 };
  if (layoutOf(parent)) { const lr = computeLayout(parent).rects[c.id]; if (lr) r = lr; }
  const cm = childMove.value;
  const ox = cm && cm.childId === c.id ? cm.dx : 0;
  const oy = cm && cm.childId === c.id ? cm.dy : 0;
  return {
    left: r.x + ox + 'px', top: r.y + oy + 'px', width: r.w + 'px', height: r.h + 'px',
    transform: r.rotation ? `rotate(${r.rotation}deg)` : undefined,
    zIndex: cm && cm.childId === c.id ? 60 : undefined,
    ...boxStyle(c), // widget card border/bg also when nested in a container
    ...visibility,
  };
}

// ── Reparent OUT: drag a SELECTED group child out (to another container or
// back to the root). Plain (unselected) child clicks fall through to the
// group move; double-click selects a child first. ────────────────────────
const childMove = ref<{ parentId: string; childId: string; start: RectPlacement; gx: number; gy: number; dx: number; dy: number } | null>(null);
function onChildDown(e: PointerEvent, parent: DesignerNode, c: DesignerNode): void {
  if (!props.interactive) {
    if (props.inspect) { e.stopPropagation(); emit('inspect', c.id); } // debug → inspect the child directly
    else if (c.events?.click?.length) { e.stopPropagation(); emit('trigger', c.id, 'click'); }
    return; // a passive child still delegates to the group's action
  }
  if (!isSel(c.id)) return; // not selected → let the group handle the move
  e.stopPropagation();
  // Nested grid group: children are CSS grid-items; free-dragging them out is a
  // follow-up. Select only (cell edits via the inspector's Col/Row/Cols/Rows).
  if (isGridGroup(parent)) return;
  (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  movePx = e.clientX; movePy = e.clientY; moveMoved = false;
  let start = c.rect ?? { x: 0, y: 0, w: 40, h: 40 };
  if (layoutOf(parent)) { const lr = computeLayout(parent).rects[c.id]; if (lr) start = lr; }
  const gr = r(parent);
  childMove.value = { parentId: parent.id, childId: c.id, start: { ...start }, gx: gr.x, gy: gr.y, dx: 0, dy: 0 };
  window.addEventListener('pointermove', onChildDrag);
  window.addEventListener('pointerup', onChildUp, { once: true });
}
function onChildDrag(e: PointerEvent): void {
  const cm = childMove.value;
  if (!cm) return;
  const dx = (e.clientX - movePx) / z(), dy = (e.clientY - movePy) / z();
  if (Math.abs(dx) > 2 || Math.abs(dy) > 2) moveMoved = true;
  childMove.value = { ...cm, dx, dy };
  const cx = cm.gx + cm.start.x + dx + cm.start.w / 2;
  const cy = cm.gy + cm.start.y + dy + cm.start.h / 2;
  const over = containerAt(cx, cy, cm.childId);
  reparentTargetId.value = over && over.id !== cm.parentId ? over.id : null;
}
function onChildUp(): void {
  window.removeEventListener('pointermove', onChildDrag);
  const cm = childMove.value;
  childMove.value = null;
  reparentTargetId.value = null;
  if (!cm || !moveMoved) return;
  const absX = Math.max(0, Math.round(cm.gx + cm.start.x + cm.dx));
  const absY = Math.max(0, Math.round(cm.gy + cm.start.y + cm.dy));
  const over = containerAt(cm.gx + cm.start.x + cm.dx + cm.start.w / 2, cm.gy + cm.start.y + cm.dy + cm.start.h / 2, cm.childId);
  if (over && over.id === cm.parentId) {
    // Stayed inside its group → just reposition (rect relative to the group).
    emit('op', { t: 'update_node', id: cm.childId, patch: { rect: { ...cm.start, x: Math.round(absX - cm.gx), y: Math.round(absY - cm.gy) } } });
  } else if (over) {
    // Reparent into a different container.
    const gr = r(over);
    emit('ops', [
      { t: 'update_node', id: cm.childId, patch: { rect: { ...cm.start, x: Math.round(absX - gr.x), y: Math.round(absY - gr.y) } } },
      { t: 'move_node', id: cm.childId, parentId: over.id, index: over.children?.length ?? 0 },
    ]);
  } else {
    // Reparent OUT to the root frame with absolute coords.
    emit('ops', [
      { t: 'update_node', id: cm.childId, patch: { rect: { ...cm.start, x: absX, y: absY } } },
      { t: 'move_node', id: cm.childId, parentId: 'root', index: props.shapes.length },
    ]);
  }
}
function r(n: DesignerNode): RectPlacement { return n.rect ?? { x: 0, y: 0, w: 80, h: 60 }; }
// Root frame auto-layout: arrange the TOP-LEVEL shapes across the full
// canvas (fixed extents) — like setting flex on the whole screen. When
// active, top-level shapes are layout-managed (not free-dragged).
const rootLayoutActive = computed(() => {
  const l = props.rootLayout as { mode?: string } | undefined;
  return !!(l && l.mode && l.mode !== 'none');
});
// Grid layout mode (dashboard-style, gridstack-like): the top-level shapes are
// laid out in a REAL CSS grid (display:grid host) and each shape is a grid-item
// placed by its `grid` cell ({x,y,w,h} in cells). Explicit placement → holes
// are allowed. Drag snaps to another cell; the SE handle changes the span.
const gridMode = computed(() => (props.rootLayout as { mode?: string } | undefined)?.mode === 'grid');
const gridSpec = computed<GridSpec>(() => gridSpecOf(props.rootLayout));
const DEFAULT_CELL: GridPlacement = { x: 0, y: 0, w: 4, h: 4 };
function gridCellOf(n: DesignerNode): GridPlacement { return n.grid ?? DEFAULT_CELL; }

// Repeat expansion (Run only): a node with props.repeat renders one instance per
// value of its variable (design_render returns each as `${id}#${value}` plus a
// base marker carrying the value list). In edit mode the base node renders as-is.
// Instances flow into consecutive cells (grid) or offset rects (free).
const renderShapes = computed<DesignerNode[]>(() => {
  if (props.interactive) return props.shapes; // editing: no expansion
  const cols = gridSpec.value.cols;
  const out: DesignerNode[] = [];
  for (const n of props.shapes) {
    const rep = eff(n).repeat;
    if (!(rep && Array.isArray(rep.values) && rep.values.length) || !P(n).repeat) { out.push(n); continue; }
    const base = gridCellOf(n);
    const gw = Math.max(1, base.w), gh = Math.max(1, base.h);
    let gx = base.x, gy = base.y;
    const baseRect = n.rect ?? { x: 0, y: 0, w: 160, h: 90 };
    rep.values.forEach((val, k) => {
      const inst: DesignerNode = { ...n, id: `${n.id}#${val}`, name: n.name ? `${n.name} · ${val}` : String(val) };
      if (gridMode.value) {
        if (gx + gw > cols) { gx = 0; gy += gh; }
        inst.grid = { x: gx, y: gy, w: gw, h: gh };
        gx += gw;
      } else {
        inst.rect = { ...baseRect, x: baseRect.x + k * (baseRect.w + 12) };
      }
      out.push(inst);
    });
  }
  return out;
});
// CSS grid host for the top-level grid (display:grid) — measured for drag math.
const gridHostEl = ref<HTMLElement | null>(null);
// Inline style for the top-level host: real grid when gridMode, else a plain
// absolute fill so the free/flex shapes keep positioning exactly as before.
const gridHostStyle = computed<Record<string, string>>(() => {
  if (!gridMode.value) return { position: 'absolute', inset: '0' };
  const s = gridSpec.value;
  const out: Record<string, string> = {
    position: 'absolute', left: '0', top: '0', right: '0',
    display: 'grid',
    gridTemplateColumns: `repeat(${s.cols}, 1fr)`,
    gridAutoRows: `${s.rowH}px`,
    columnGap: `${s.colGap}px`,
    rowGap: `${s.rowGap}px`,
  };
  return out;
});

// ── Grid drag / resize (cell snapping, gridstack-style) ─────────────────
// Parallel to the absolute free-drag machinery; only active in grid mode. Move
// relocates the cell (x,y); resize (SE handle) changes the span (w,h). The live
// `cur` cell drives shapeStyle for an in-place preview; pointer-up emits the op.
const gridDrag = ref<{ id: string; mode: 'move' | 'resize'; start: GridPlacement; cur: GridPlacement; px: number; py: number } | null>(null);
// While grid-dragging, the resolved cells for EVERY top-level item (the moved
// one at its live cell + the others pushed out of the way). Holes are kept.
const gridPreview = ref<Record<string, GridPlacement> | null>(null);

// gridstack-style collision resolution: the moved item is anchored at
// `movedCell`; any item it (or a pushed item) overlaps is pushed DOWN to just
// below the collider. Pushes only (no upward compaction) so intentional holes
// survive. Returns the new cell for every top-level item.
function reflowGrid(movedId: string, movedCell: GridPlacement): Record<string, GridPlacement> {
  const items = props.shapes.map((n) =>
    n.id === movedId ? { id: n.id, ...movedCell } : { id: n.id, ...gridCellOf(n) },
  );
  const hit = (a: GridPlacement, b: GridPlacement): boolean =>
    a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
  const moved = items.find((i) => i.id === movedId)!;
  const placed: GridPlacement[] = [moved];
  // Place the rest top-to-bottom, bumping each below any already-placed collider.
  for (const n of items.filter((i) => i.id !== movedId).sort((a, b) => a.y - b.y || a.x - b.x)) {
    let bumped = true, guard = 0;
    while (bumped && guard++ < 500) {
      bumped = false;
      for (const p of placed) {
        if (hit(n, p)) { n.y = p.y + p.h; bumped = true; break; }
      }
    }
    placed.push(n);
  }
  const out: Record<string, GridPlacement> = {};
  for (const i of items) out[i.id] = { x: i.x, y: i.y, w: i.w, h: i.h };
  return out;
}
function gridColW(): number {
  const s = gridSpec.value;
  const w = gridHostEl.value?.clientWidth ?? (props.frame?.w ?? 1280);
  return (w - s.colGap * (s.cols - 1)) / s.cols;
}
function beginGridDrag(e: PointerEvent, n: DesignerNode, mode: 'move' | 'resize'): void {
  (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  const start = { ...gridCellOf(n) };
  gridDrag.value = { id: n.id, mode, start, cur: { ...start }, px: e.clientX, py: e.clientY };
  window.addEventListener('pointermove', onGridDrag);
  window.addEventListener('pointerup', onGridUp, { once: true });
}
function onGridDrag(e: PointerEvent): void {
  const gd = gridDrag.value;
  if (!gd) return;
  const s = gridSpec.value;
  const stepX = (gridColW() + s.colGap) / z();
  const stepY = (s.rowH + s.rowGap) / z();
  const dx = Math.round((e.clientX - gd.px) / stepX);
  const dy = Math.round((e.clientY - gd.py) / stepY);
  const st = gd.start;
  if (gd.mode === 'move') {
    gd.cur = {
      x: Math.max(0, Math.min(s.cols - st.w, st.x + dx)),
      y: Math.max(0, st.y + dy),
      w: st.w, h: st.h,
    };
  } else {
    gd.cur = {
      x: st.x, y: st.y,
      w: Math.max(1, Math.min(s.cols - st.x, st.w + dx)),
      h: Math.max(1, st.h + dy),
    };
  }
  // gridstack-style: push the other panels out of the moved/resized one's way.
  gridPreview.value = reflowGrid(gd.id, gd.cur);
}
function onGridUp(): void {
  window.removeEventListener('pointermove', onGridDrag);
  const gd = gridDrag.value;
  const preview = gridPreview.value;
  gridDrag.value = null;
  gridPreview.value = null;
  if (!gd) return;
  // Commit the moved item plus every panel the reflow pushed: one op per node
  // whose resolved cell differs from what's stored.
  const resolved = preview ?? { [gd.id]: gd.cur };
  const ops: DesignerOp[] = [];
  for (const n of props.shapes) {
    const next = resolved[n.id];
    if (!next) continue;
    const cur = gridCellOf(n);
    if (cur.x !== next.x || cur.y !== next.y || cur.w !== next.w || cur.h !== next.h) {
      ops.push({ t: 'update_node', id: n.id, patch: { grid: next } });
    }
  }
  if (ops.length === 1) emit('op', ops[0]!);
  else if (ops.length > 1) emit('ops', ops);
}
// Panel title for grid mode: the node's name, else a label by shape/kind.
const PANEL_LABELS: Record<string, string> = {
  kpi: 'KPI', gauge: 'Gauge', chart: 'Chart', table: 'Table', value: 'Value', progress: 'Progress',
  level: 'Level', map: 'Map', plugin: 'Widget', embed: 'HTML', slider: 'Slider', toggle: 'Toggle', input: 'Input',
};
function panelTitle(n: DesignerNode): string {
  const t = panelOf(n)?.title;
  if (t) return t;
  if (n.name) return n.name;
  if (n.kind === 'group') return 'Group';
  const s = (n.props as { shape?: string } | undefined)?.shape ?? '';
  return PANEL_LABELS[s] ?? 'Panel';
}
// Right-side type tag (like the dashboard's panel-type chip): the shape kind.
function panelType(n: DesignerNode): string {
  if (n.kind === 'group') return 'group';
  return (n.props as { shape?: string } | undefined)?.shape ?? '';
}
// Per-node panel chrome (grid mode). `show` defaults to true so existing docs
// keep their card look; setting it false renders the element bare in its cell.
function panelOf(n: DesignerNode): PanelSpec | undefined {
  return (n.props as { panel?: PanelSpec } | undefined)?.panel;
}
function panelShown(n: DesignerNode): boolean {
  if (!gridMode.value) return false;
  return panelOf(n)?.show !== false;
}
// Header style: title alignment + optional title color; type chip stays right.
function panelNameStyle(n: DesignerNode): Record<string, string | undefined> {
  const p = panelOf(n);
  return { textAlign: p?.align, color: p?.titleColor };
}

// Flex root layout (row/column) only — top-level shapes are positioned by the
// auto-layout engine. Grid mode is NOT handled here: it renders as a real CSS
// grid (display:grid host + grid-item shapes placed by cell), see shapeStyle.
const rootRects = computed<Record<string, RectPlacement> | null>(() => {
  if (!rootLayoutActive.value || gridMode.value) return null;
  const frame = props.frame ?? { x: 0, y: 0, w: 1280, h: 720 };
  const synthetic = { id: 'root', kind: 'group', rect: frame, props: { layout: { ...(props.rootLayout as object), wMode: 'fixed', hMode: 'fixed' } }, children: props.shapes } as unknown as DesignerNode;
  return computeLayout(synthetic).rects;
});

function shapeStyle(n: DesignerNode): Record<string, string | undefined> {
  // Grid mode: top-level node is a CSS grid-item placed by its cell. The live
  // cell (while grid-dragging/resizing THIS node) gives an in-place preview.
  if (gridMode.value) {
    const g = gridPreview.value?.[n.id]
      ?? ((gridDrag.value && gridDrag.value.id === n.id) ? gridDrag.value.cur : gridCellOf(n));
    const e0 = eff(n);
    const hidden0 = e0?.hidden || (props.interactive && P(n).hidden === true);
    const panelBg = panelShown(n) ? panelOf(n)?.bg : undefined;
    return {
      gridColumn: `${g.x + 1} / span ${g.w}`,
      gridRow: `${g.y + 1} / span ${g.h}`,
      opacity: e0 && e0.opacity !== undefined ? String(e0.opacity) : (P(n).opacity != null ? String(P(n).opacity) : undefined),
      mixBlendMode: (P(n).blendMode as string | undefined),
      display: hidden0 ? 'none' : undefined,
      filter: shadowFilter(n),
      ...boxStyle(n),
      ...(panelBg ? { background: panelBg } : {}),
      ...(customAnimStyle(n) ?? {}),
    };
  }
  let live = r(n);
  // Root auto-layout positions top-level shapes.
  const rr = rootRects.value?.[n.id];
  if (rr) live = { ...live, x: rr.x, y: rr.y, w: rr.w, h: rr.h };
  // Auto-layout container hugs its content.
  if (n.kind === 'group' && layoutOf(n)) { const s = computeLayout(n).size; live = { ...live, w: s.w, h: s.h }; }
  if (resize.value && resize.value.id === n.id) live = resize.value.cur;
  else if (moving.value && isSel(n.id)) live = { ...live, x: Math.max(0, live.x + moving.value.dx), y: Math.max(0, live.y + moving.value.dy) };
  const e = eff(n);
  const hidden = e?.hidden || (props.interactive && P(n).hidden === true); // design-time hide (layers eye)
  return {
    left: live.x + 'px', top: live.y + 'px', width: live.w + 'px', height: live.h + 'px',
    transform: live.rotation ? `rotate(${live.rotation}deg)` : undefined,
    opacity: e && e.opacity !== undefined ? String(e.opacity) : (P(n).opacity != null ? String(P(n).opacity) : undefined),
    mixBlendMode: (P(n).blendMode as string | undefined),
    display: hidden ? 'none' : undefined,
    filter: shadowFilter(n),
    ...boxStyle(n),
    ...(customAnimStyle(n) ?? {}),
  };
}
// Card box (border + optional bg + radius) for data widgets — the base
// style's Borde/Grosor/Radio + a "fondo" become a box around the widget
// (KPI/gauge/progress/chart have no box of their own otherwise).
const WIDGET_BOX = ['kpi', 'gauge', 'progress', 'level', 'chart', 'carousel'];
const GENERIC_BOX = ['value', 'text', 'image', 'embed', 'plugin', 'video', 'slider', 'toggle', 'input', 'select'];
/** Container (group) background box: fill + border (uniform or per-side). */
function grpStyle(n: DesignerNode): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = { background: fill(n), borderRadius: radiusCss(radiusOf(n), 6) };
  if (!P(n).stroke) { out.border = 'none'; return out; }
  const col = stroke(n) as string, st = (P(n).strokeStyle as string) || 'solid';
  const sides = P(n).borderSides as { t?: number; r?: number; b?: number; l?: number } | undefined;
  if (sides) {
    out.borderStyle = st; out.borderColor = col;
    out.borderTopWidth = (sides.t ?? 0) + 'px'; out.borderRightWidth = (sides.r ?? 0) + 'px';
    out.borderBottomWidth = (sides.b ?? 0) + 'px'; out.borderLeftWidth = (sides.l ?? 0) + 'px';
  } else out.border = `${P(n).strokeWidth ?? 1}px ${st} ${col}`;
  return out;
}
function boxStyle(n: DesignerNode): Record<string, string | undefined> {
  const k = kind(n);
  const widget = WIDGET_BOX.includes(k);
  if (!widget && !GENERIC_BOX.includes(k)) return {};
  const sw = Number(strokeWidthOf(n) ?? 0);
  const sides = borderSidesOf(n) as { t?: number; r?: number; b?: number; l?: number } | undefined;
  const bg = (eff(n).bg ?? P(n).bg) as string | undefined; // universal box background
  if (!sw && !sides && !bg) return {};
  const out: Record<string, string | undefined> = {};
  const col = (widget ? (tok(P(n).borderColor) ?? P(n).borderColor ?? '#e5e7eb') : (stroke(n) ?? '#6b7280')) as string;
  const st = (strokeStyleOf(n) as string) || 'solid';
  if (sides) {
    out.borderStyle = st; out.borderColor = col;
    out.borderTopWidth = (sides.t ?? 0) + 'px'; out.borderRightWidth = (sides.r ?? 0) + 'px';
    out.borderBottomWidth = (sides.b ?? 0) + 'px'; out.borderLeftWidth = (sides.l ?? 0) + 'px';
  } else if (sw > 0) out.border = `${sw}px ${st} ${col}`;
  if (bg) out.background = tok(bg) ?? bg;
  if (sw > 0 || bg || sides) out.borderRadius = radiusCss(radiusOf(n), widget ? 8 : 0);
  return out;
}

// ── Selection + move (multi, with snapping D1.2) ───────────────────────
const moving = ref<{ dx: number; dy: number } | null>(null);
const guides = ref<{ vx: number[]; hy: number[] }>({ vx: [], hy: [] });
// Reparenting (drag a node into a container/group): the highlighted target.
const reparentTargetId = ref<string | null>(null);
/** Topmost top-level container (group OR carousel widget) whose box contains
 *  (cx,cy), excluding `exclude` — drop a shape onto it to nest/add it. */
function containerAt(cx: number, cy: number, exclude: string): DesignerNode | null {
  for (let i = props.shapes.length - 1; i >= 0; i--) {
    const g = props.shapes[i]!;
    if (g.id === exclude || (g.kind !== 'group' && P(g).shape !== 'carousel')) continue;
    const rr = r(g);
    if (cx >= rr.x && cx <= rr.x + rr.w && cy >= rr.y && cy <= rr.y + rr.h) return g;
  }
  return null;
}
const resize = ref<{ id: string; start: RectPlacement; cur: RectPlacement; px: number; py: number; dir: HandleDir; cx: number; cy: number } | null>(null);
let movePx = 0, movePy = 0, moveMoved = false;
const SNAP = 6, GRID = 8;
interface MoveCtx { bbox: RectPlacement; tx: number[]; ty: number[] }
let moveCtx: MoveCtx | null = null;

function bboxOf(ids: Set<string>): RectPlacement {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const n of props.shapes) { if (!ids.has(n.id)) continue; const rr = r(n); x0 = Math.min(x0, rr.x); y0 = Math.min(y0, rr.y); x1 = Math.max(x1, rr.x + rr.w); y1 = Math.max(y1, rr.y + rr.h); }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

function onDown(e: PointerEvent, n: DesignerNode) {
  if (!props.interactive) {
    if (props.inspect) { emit('inspect', n.id); return; } // debug mode → inspect, don't fire events
    if (e.button !== 0) return;
    if (openEquipment(e,n,'control')) return;
    emit('trigger', n.id, 'click'); return; // run mode → fire events
  }
  if (e.shiftKey) {
    const next = isSel(n.id) ? props.selectedIds.filter((id) => id !== n.id) : [...props.selectedIds, n.id];
    emit('select-set', next);
    return;
  }
  if (!isSel(n.id)) emit('select-set', [n.id]);
  // Grid mode: top-level nodes are CSS grid-items → start a cell-snapping
  // grid-drag (its own path, parallel to the absolute free-drag below).
  if (gridMode.value) { beginGridDrag(e, n, 'move'); return; }
  // Flex auto-layout (row/column) manages positions → select only, no free drag.
  if (rootLayoutActive.value) return;
  (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  movePx = e.clientX; movePy = e.clientY; moveMoved = false;
  // Capture moving set + static snap targets (edges/centres of other shapes).
  const ids = new Set(isSel(n.id) ? props.selectedIds : [n.id]);
  const tx: number[] = [], ty: number[] = [];
  for (const s of props.shapes) {
    if (ids.has(s.id)) continue;
    const rr = r(s); tx.push(rr.x, rr.x + rr.w / 2, rr.x + rr.w); ty.push(rr.y, rr.y + rr.h / 2, rr.y + rr.h);
  }
  moveCtx = { bbox: bboxOf(ids), tx, ty };
  moving.value = { dx: 0, dy: 0 };
  window.addEventListener('pointermove', onMoveDrag);
  window.addEventListener('pointerup', onMoveUp, { once: true });
}
/** Snap one axis: try bbox left/centre/right (or top/centre/bottom)
 *  against static targets; fall back to the grid. Returns {delta, guide}. */
function snapAxis(origin: number, size: number, raw: number, targets: number[]): { delta: number; guide: number | null } {
  const cands = [origin + raw, origin + raw + size / 2, origin + raw + size];
  let best: { adj: number; guide: number } | null = null;
  for (const c of cands) for (const t of targets) {
    const diff = t - c;
    if (Math.abs(diff) <= SNAP && (!best || Math.abs(diff) < Math.abs(best.adj))) best = { adj: diff, guide: t };
  }
  if (best) return { delta: raw + best.adj, guide: best.guide };
  const snappedLeft = Math.round((origin + raw) / GRID) * GRID; // grid fallback
  return { delta: snappedLeft - origin, guide: null };
}
function onMoveDrag(e: PointerEvent) {
  if (!moving.value || !moveCtx) return;
  let dx = (e.clientX - movePx) / z(), dy = (e.clientY - movePy) / z();
  if (Math.abs(dx) > 2 || Math.abs(dy) > 2) moveMoved = true;
  if (props.snap !== false) {
    const sx = snapAxis(moveCtx.bbox.x, moveCtx.bbox.w, dx, moveCtx.tx);
    const sy = snapAxis(moveCtx.bbox.y, moveCtx.bbox.h, dy, moveCtx.ty);
    dx = sx.delta; dy = sy.delta;
    guides.value = { vx: sx.guide !== null ? [sx.guide] : [], hy: sy.guide !== null ? [sy.guide] : [] };
  } else {
    dx = Math.round(dx); dy = Math.round(dy);
    guides.value = { vx: [], hy: [] };
  }
  moving.value = { dx, dy };
  // Highlight a container under a single dragged (non-group) shape.
  if (props.selectedIds.length === 1) {
    const n = props.shapes.find((s) => s.id === props.selectedIds[0]);
    if (n && n.kind !== 'group') {
      const cx = moveCtx.bbox.x + dx + moveCtx.bbox.w / 2;
      const cy = moveCtx.bbox.y + dy + moveCtx.bbox.h / 2;
      reparentTargetId.value = containerAt(cx, cy, n.id)?.id ?? null;
    } else reparentTargetId.value = null;
  }
}
function onMoveUp() {
  window.removeEventListener('pointermove', onMoveDrag);
  const m = moving.value;
  const tgt = reparentTargetId.value;
  moving.value = null;
  guides.value = { vx: [], hy: [] };
  reparentTargetId.value = null;
  moveCtx = null;
  if (!m || !moveMoved) return;
  // Dropped onto a container → reparent: move into the group + rebase the
  // rect to be relative to the group box (skips the plain move below).
  if (tgt && props.selectedIds.length === 1) {
    const n = props.shapes.find((s) => s.id === props.selectedIds[0]);
    const g = props.shapes.find((s) => s.id === tgt);
    if (n && g && n.kind !== 'group') {
      const rr = r(n);
      const absX = Math.max(0, Math.round(rr.x + m.dx));
      const absY = Math.max(0, Math.round(rr.y + m.dy));
      const gr = r(g);
      emit('ops', [
        { t: 'update_node', id: n.id, patch: { rect: { ...rr, x: Math.round(absX - gr.x), y: Math.round(absY - gr.y) } } },
        { t: 'move_node', id: n.id, parentId: g.id, index: g.children?.length ?? 0 },
      ]);
      return;
    }
  }
  // Commit a batch of rect moves for every selected shape.
  const ids = new Set(props.selectedIds);
  const ops: DesignerOp[] = [];
  for (const n of props.shapes) {
    if (!ids.has(n.id)) continue;
    const rr = r(n);
    ops.push({ t: 'update_node', id: n.id, patch: { rect: { ...rr, x: Math.max(0, Math.round(rr.x + m.dx)), y: Math.max(0, Math.round(rr.y + m.dy)) } } });
  }
  if (ops.length === 1) emit('op', ops[0]!);
  else if (ops.length > 1) emit('ops', ops);
}

function onHandle(e: PointerEvent, n: DesignerNode, dir: HandleDir) {
  if (!props.interactive) return;
  // Grid mode shows only the SE handle → it resizes the cell span.
  if (gridMode.value) { beginGridDrag(e, n, 'resize'); return; }
  const el = (e.target as HTMLElement).parentElement; // the shape div
  const box = el?.getBoundingClientRect();
  const cx = box ? box.left + box.width / 2 : e.clientX;
  const cy = box ? box.top + box.height / 2 : e.clientY;
  (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  const start = { ...r(n) };
  resize.value = { id: n.id, start, cur: { ...start }, px: e.clientX, py: e.clientY, dir, cx, cy };
  window.addEventListener('pointermove', onResizeDrag);
  window.addEventListener('pointerup', onResizeUp, { once: true });
}
function onResizeDrag(e: PointerEvent) {
  const rz = resize.value;
  if (!rz) return;
  const s = rz.start;
  if (rz.dir === 'rotate') {
    let ang = (Math.atan2(e.clientY - rz.cy, e.clientX - rz.cx) * 180) / Math.PI + 90;
    if (e.shiftKey) ang = Math.round(ang / 15) * 15;
    rz.cur = { ...s, rotation: Math.round(ang) };
    return;
  }
  const dx = (e.clientX - rz.px) / z(), dy = (e.clientY - rz.py) / z();
  let { x, y, w, h } = s;
  if (rz.dir.includes('e')) w = s.w + dx;
  if (rz.dir.includes('s')) h = s.h + dy;
  if (rz.dir.includes('w')) { w = s.w - dx; x = s.x + dx; }
  if (rz.dir.includes('n')) { h = s.h - dy; y = s.y + dy; }
  // Clamp to a min size without letting the anchored edge drift.
  if (w < 8) { if (rz.dir.includes('w')) x -= 8 - w; w = 8; }
  if (h < 8) { if (rz.dir.includes('n')) y -= 8 - h; h = 8; }
  rz.cur = { ...s, x: Math.max(0, x), y: Math.max(0, y), w, h };
}
function onResizeUp() {
  window.removeEventListener('pointermove', onResizeDrag);
  const rz = resize.value;
  resize.value = null;
  if (!rz) return;
  const a = rz.start, b = rz.cur;
  if (!(a.x !== b.x || a.y !== b.y || a.w !== b.w || a.h !== b.h || a.rotation !== b.rotation)) return;
  const patch: Record<string, unknown> = { rect: { ...b, x: Math.round(b.x), y: Math.round(b.y), w: Math.round(b.w), h: Math.round(b.h) } };
  // Resizing an auto-layout container pins the changed axis to fixed size.
  const node = props.shapes.find((s) => s.id === rz.id);
  const lay = node && node.kind === 'group' ? layoutOf(node) : null;
  if (lay) {
    const nl = { ...lay };
    if (Math.round(a.w) !== Math.round(b.w)) nl.wMode = 'fixed';
    if (Math.round(a.h) !== Math.round(b.h)) nl.hMode = 'fixed';
    patch.props = { layout: nl };
  }
  emit('op', { t: 'update_node', id: rz.id, patch });
}

// ── Marquee selection ──────────────────────────────────────────────────
const marquee = ref<{ x0: number; y0: number; x1: number; y1: number; add: boolean } | null>(null);
const marqueeStyle = computed(() => {
  const m = marquee.value;
  if (!m) return {};
  return { left: Math.min(m.x0, m.x1) + 'px', top: Math.min(m.y0, m.y1) + 'px', width: Math.abs(m.x1 - m.x0) + 'px', height: Math.abs(m.y1 - m.y0) + 'px' };
});
function localPoint(e: { clientX: number; clientY: number }): { x: number; y: number } {
  const rect = frameEl.value!.getBoundingClientRect();
  return { x: (e.clientX - rect.left) / z(), y: (e.clientY - rect.top) / z() };
}

// ── Path/polygon vertex editing ─────────────────────────────────────────
const vtx = ref<{ id: string; idx: number; points: Vec[]; p0: Vec; sx: number; sy: number; rw: number; rh: number } | null>(null);
function clamp01k(v: number): number { return Math.max(0, Math.min(1000, v)); }
function onVertexDown(e: PointerEvent, n: DesignerNode, idx: number): void {
  if (!props.interactive) return;
  (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  const r0 = r(n);
  const points = ((P(n).points as Vec[] | undefined) ?? []).map((p) => ({ ...p }));
  vtx.value = { id: n.id, idx, points, p0: { ...points[idx]! }, sx: e.clientX, sy: e.clientY, rw: r0.w || 1, rh: r0.h || 1 };
  window.addEventListener('pointermove', onVertexDrag);
  window.addEventListener('pointerup', onVertexUp, { once: true });
}
function onVertexDrag(e: PointerEvent): void {
  const v = vtx.value;
  if (!v) return;
  const nx = clamp01k(v.p0.x + (e.clientX - v.sx) / z() / v.rw * 1000);
  const ny = clamp01k(v.p0.y + (e.clientY - v.sy) / z() / v.rh * 1000);
  vtx.value = { ...v, points: v.points.map((p, i) => (i === v.idx ? { x: Math.round(nx), y: Math.round(ny) } : p)) };
}
function onVertexUp(): void {
  window.removeEventListener('pointermove', onVertexDrag);
  const v = vtx.value;
  vtx.value = null;
  if (v) emit('op', { t: 'update_node', id: v.id, patch: { props: { points: v.points } } });
}
function removeVertex(n: DesignerNode, idx: number): void {
  const points = ((P(n).points as Vec[] | undefined) ?? []);
  if (points.length <= 2) return; // keep at least a segment
  emit('op', { t: 'update_node', id: n.id, patch: { props: { points: points.filter((_, i) => i !== idx) } } });
}
/** Distance² from point p to segment a-b (for nearest-edge insertion). */
function distToSeg(p: Vec, a: Vec, b: Vec): number {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len2 = dx * dx + dy * dy || 1;
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = a.x + t * dx, cy = a.y + t * dy;
  return (p.x - cx) ** 2 + (p.y - cy) ** 2;
}
/** Child dblclick: in EDIT mode select the child and stop propagation (so the
 *  shape's own dblclick handler doesn't also run). In RUN mode do nothing and
 *  let the event bubble to the shape root → onShapeDblclick fires the run-mode
 *  'dblclick' trigger (a .stop here used to kill dblclick events on children). */
function onChildDblclick(e: MouseEvent, c: DesignerNode): void {
  if (!props.interactive) {
    if (c.events?.dblclick?.length) { e.stopPropagation(); emit('trigger', c.id, 'dblclick'); }
    return;
  }
  e.stopPropagation();
  emit('select-set', [c.id]);
}
function onShapeDblclick(e: MouseEvent, n: DesignerNode): void {
  if (!props.interactive) { emit('trigger', n.id, 'dblclick'); return; }
  if (kind(n) !== 'path') return;
  const rr = r(n);
  const fp = localPoint(e as unknown as PointerEvent);
  const np: Vec = { x: Math.round(clamp01k((fp.x - rr.x) / (rr.w || 1) * 1000)), y: Math.round(clamp01k((fp.y - rr.y) / (rr.h || 1) * 1000)) };
  const points = ((P(n).points as Vec[] | undefined) ?? []).map((p) => ({ ...p }));
  if (points.length < 2) { points.push(np); }
  else {
    // Insert on the nearest edge (incl. the closing edge when closed).
    let best = 0, bestD = Infinity;
    const segs = P(n).closed === true ? points.length : points.length - 1;
    for (let i = 0; i < segs; i++) {
      const d = distToSeg(np, points[i]!, points[(i + 1) % points.length]!);
      if (d < bestD) { bestD = d; best = i; }
    }
    points.splice(best + 1, 0, np);
  }
  emit('op', { t: 'update_node', id: n.id, patch: { props: { points } } });
}

// ── Pan + wheel-zoom (navigate a canvas larger than the viewport) ───────
const panning = ref(false);
const spaceDown = ref(false);
function onWrapDown(e: PointerEvent): void {
  // Middle-mouse, or Space+left-drag → pan by scrolling the wrap.
  const wantPan = e.button === 1 || (spaceDown.value && e.button === 0);
  if (!wantPan || !wrap.value) return;
  e.preventDefault(); e.stopPropagation();
  panning.value = true;
  const sx = e.clientX, sy = e.clientY, sl = wrap.value.scrollLeft, st = wrap.value.scrollTop;
  const move = (ev: PointerEvent) => { if (wrap.value) { wrap.value.scrollLeft = sl - (ev.clientX - sx); wrap.value.scrollTop = st - (ev.clientY - sy); } };
  const up = () => { panning.value = false; window.removeEventListener('pointermove', move); panMove = null; window.removeEventListener('pointerup', up); };
  panMove = move;
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up, { once: true });
}
// Active pan-drag listener (closure) so unmount can also clean it up.
let panMove: ((ev: PointerEvent) => void) | null = null;
function onWheel(e: WheelEvent): void {
  if (!props.scaleSelf) return; // plain wheel scrolls (default)
  if (e.ctrlKey || e.metaKey) { e.preventDefault(); setZoom(zoomSelf.value + (e.deltaY < 0 ? 0.1 : -0.1)); }
}
function isTyping(e: KeyboardEvent): boolean {
  const t = (e.target as HTMLElement)?.tagName;
  return t === 'INPUT' || t === 'TEXTAREA' || t === 'SELECT';
}
function onSpaceKey(e: KeyboardEvent): void {
  if (e.code !== 'Space' || isTyping(e)) return;
  if (e.type === 'keydown') { spaceDown.value = true; e.preventDefault(); } else { spaceDown.value = false; }
}

// ── Drag & drop from the palette (Figma-style) ──────────────────────────
const DRAG_MIME = 'application/x-lymon-palette';
const dropActive = ref(false);
function hasPalette(e: DragEvent): boolean { return !!e.dataTransfer && Array.from(e.dataTransfer.types).includes(DRAG_MIME); }
function onDragEnter(e: DragEvent): void { if (props.interactive && hasPalette(e)) dropActive.value = true; }
function onDragOver(e: DragEvent): void {
  if (!props.interactive || !hasPalette(e)) return;
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  dropActive.value = true;
}
function onDragLeave(e: DragEvent): void {
  // Only clear when the cursor actually leaves the frame (not a child).
  if (!frameEl.value || frameEl.value.contains(e.relatedTarget as Node)) return;
  dropActive.value = false;
}
function onDrop(e: DragEvent): void {
  dropActive.value = false;
  if (!props.interactive) return;
  const data = e.dataTransfer?.getData(DRAG_MIME);
  if (!data) return;
  emit('palette-drop', data, localPoint(e));
}
function onFrameDown(e: PointerEvent) {
  if (!props.interactive) return;
  if (props.commentMode) { emit('comment-at', localPoint(e)); return; } // drop a comment pin
  const p = localPoint(e);
  marquee.value = { x0: p.x, y0: p.y, x1: p.x, y1: p.y, add: e.shiftKey };
  window.addEventListener('pointermove', onMarqueeMove);
  window.addEventListener('pointerup', onMarqueeUp, { once: true });
}
function onMarqueeMove(e: PointerEvent) {
  if (!marquee.value) return;
  const p = localPoint(e);
  marquee.value.x1 = p.x; marquee.value.y1 = p.y;
}
function onMarqueeUp() {
  window.removeEventListener('pointermove', onMarqueeMove);
  const m = marquee.value;
  marquee.value = null;
  if (!m) return;
  const x = Math.min(m.x0, m.x1), y = Math.min(m.y0, m.y1), w = Math.abs(m.x1 - m.x0), h = Math.abs(m.y1 - m.y0);
  if (w < 4 && h < 4) { if (!m.add) emit('select-set', []); return; } // a plain click clears
  const hit = props.shapes.filter((n) => { const rr = r(n); return rr.x < x + w && rr.x + rr.w > x && rr.y < y + h && rr.y + rr.h > y; }).map((n) => n.id);
  emit('select-set', m.add ? [...new Set([...props.selectedIds, ...hit])] : hit);
}

onBeforeUnmount(() => {
  if (panMove) { window.removeEventListener('pointermove', panMove); panMove = null; }
  window.removeEventListener('pointermove', onMoveDrag);
  window.removeEventListener('pointermove', onResizeDrag);
  window.removeEventListener('pointermove', onMarqueeMove);
  window.removeEventListener('pointermove', onChildDrag);
  window.removeEventListener('pointermove', onVertexDrag);
  window.removeEventListener('pointermove', onGridDrag);
  window.removeEventListener('keydown', onSpaceKey);
  window.removeEventListener('keyup', onSpaceKey);
  fitRO?.disconnect();
  for (const id in carTimers) clearInterval(carTimers[id]!);
});
</script>

<style scoped>
.canvas-host { position: relative; flex: 1 1 0; display: flex; min-width: 0; min-height: 0; }
.syn-wrap { flex: 1; overflow: auto; padding: 24px; background:
  radial-gradient(circle, var(--color-line) 1px, transparent 1px) 0 0 / 20px 20px, var(--color-surface-2); }
.syn-wrap.grab { cursor: grab; }
.syn-wrap.grabbing { cursor: grabbing; }
/* Kiosk fit: contain + centre the scaled frame, no scroll. */
.syn-wrap.fit { overflow: hidden; padding: 0; display: flex; align-items: center; justify-content: center; }
.zoom-pad { position: relative; }
.zoombar { position: absolute; left: 12px; bottom: 12px; z-index: 30; display: flex; align-items: center; gap: 1px;
  background: var(--color-surface); border: 1px solid var(--color-line); border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,.12); padding: 2px; }
.zoombar button { border: none; background: none; cursor: pointer; font-size: .8rem; color: var(--color-ink); padding: 4px 8px; border-radius: 6px; }
.zoombar button:hover { background: var(--color-surface-2); }
.zoombar .zlbl { min-width: 46px; font-variant-numeric: tabular-nums; }
.zoombar .zfit { border-left: 1px solid var(--color-line); }
.frame { position: relative; background: #fff; border: 1px solid var(--color-line); box-shadow: 0 1px 4px rgba(0,0,0,.06); }
.frame.drop-target { outline: 2px dashed var(--color-brand-600); outline-offset: -2px; background: #fafaff; }
.frame-grid { position: absolute; inset: 0; pointer-events: none; z-index: 0;
  background-image: linear-gradient(var(--color-line) 1px, transparent 1px), linear-gradient(90deg, var(--color-line) 1px, transparent 1px);
  background-size: 8px 8px; }
/* Grid mode: each top-level shape reads as a dashboard panel (card + header),
   matching the dashboard designer's .panel chrome. Gaps between cards show the
   dark canvas (frame bg = surface-2 in grid mode). */
.shape-body { position: absolute; inset: 0; }
/* Shapes host: plain absolute fill (free/flex). In grid mode an inline style
   turns it into display:grid (see gridHostStyle). */
.shape-host { position: absolute; inset: 0; }
/* Grid-item: placed by gridColumn/gridRow → relative, not absolute, so the CSS
   grid controls its geometry. Geometry only; chrome lives in .grid-panel. */
.shape.grid-item { position: relative; overflow: hidden; box-sizing: border-box; min-width: 0; min-height: 0; }
/* Grid-panel: the optional card + title chrome around a grid item. */
.shape.grid-panel { background: var(--color-surface); border: 1px solid var(--color-line); border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0,0,0,.04); }
.shape.grid-panel > .shape-body { top: 28px; }
.grid-panel-head { position: absolute; top: 0; left: 0; right: 0; height: 28px; display: flex; align-items: center;
  justify-content: space-between; gap: 6px; padding: 0 8px; background: var(--color-surface-2);
  border-bottom: 1px solid var(--color-line); z-index: 1; }
.grid-panel-head .gp-name { flex: 1; min-width: 0; font-size: .75rem; font-weight: 600; color: var(--color-ink);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.grid-panel-head .gp-type { flex-shrink: 0; font-size: .62rem; color: var(--color-muted);
  background: var(--color-brand-50); border-radius: 4px; padding: 1px 6px; }
.ruler { position: absolute; background: var(--color-surface-2); color: var(--color-muted); font-size: 8px; pointer-events: none; z-index: 47; }
.ruler.rh { top: -15px; left: 0; right: 0; height: 14px; border-bottom: 1px solid var(--color-line); }
.ruler.rv { top: 0; bottom: 0; left: -15px; width: 14px; border-right: 1px solid var(--color-line); }
.ruler .rt { position: absolute; }
.ruler.rh .rt { top: 0; height: 14px; border-left: 1px solid var(--color-line-strong); padding-left: 2px; line-height: 14px; }
.ruler.rv .rt { left: 0; width: 14px; border-top: 1px solid var(--color-line-strong); padding-left: 1px; }
.shape { position: absolute; user-select: none; cursor: move; }
.shape.locked { cursor: default; }
.shape.group { outline: 1px dashed transparent; }
.shape.group.selected { outline: 2px solid var(--color-brand-600); }
.shape.group.reparent-into { outline: 2px solid #22c55e; background: rgba(34,197,94,.07); }
.shape.selected { outline: 2px solid var(--color-brand-600); outline-offset: 1px; }
.child { position: absolute; }
/* Nested grid group: the body is display:grid and its children are grid-items
   placed by gridColumn/gridRow → static (the grid controls geometry). */
.shape-body.grid-body > .child { position: static; min-width: 0; min-height: 0; }
.car-nav { position: absolute; bottom: 4px; left: 50%; transform: translateX(-50%); z-index: 50; display: flex; align-items: center; gap: 6px; background: var(--color-surface); border: 1px solid var(--color-line); border-radius: 9999px; padding: 1px 8px; font-size: .7rem; color: var(--color-ink); box-shadow: 0 1px 4px rgba(0,0,0,.15); font-variant-numeric: tabular-nums; }
.car-nav button { border: none; background: none; cursor: pointer; font-size: 1rem; line-height: 1; color: var(--color-ink); padding: 0 2px; }
.car-nav button:hover { color: var(--color-brand-700); }
.grp-bg { position: absolute; inset: 0; }
.child-sel { outline: 1.5px dashed var(--color-brand-600); outline-offset: 1px; }
.marquee { position: absolute; z-index: 40; background: rgba(44,112,185,.12); border: 1px solid var(--color-brand-600); pointer-events: none; }
.cmt-pin { position: absolute; z-index: 48; transform: translate(-4px, -22px); font-size: 16px; cursor: pointer; filter: drop-shadow(0 1px 2px rgba(0,0,0,.3)); }
.cmt-pin.done { opacity: .4; }
.guide { position: absolute; z-index: 45; background: #ec4899; pointer-events: none; }
.guide.v { top: 0; bottom: 0; width: 1px; }
.guide.h { left: 0; right: 0; height: 1px; }
.peer { position: absolute; pointer-events: none; z-index: 50; transform: translate(-1px, -1px); }
.peer-tag { position: absolute; left: 12px; top: 12px; color: #fff; font-size: .62rem; padding: 1px 5px; border-radius: 4px; white-space: nowrap; }
/* Inspect (debug) mode: crosshair + outline any shape/child on hover so the
 * user can see exactly which (possibly nested) element they'll inspect. */
.syn-wrap.inspect .shape, .syn-wrap.inspect .child { cursor: crosshair; }
.syn-wrap.inspect .shape:hover, .syn-wrap.inspect .child:hover { outline: 1.5px solid var(--color-brand-600); outline-offset: 1px; }
.inspect-on { outline: 2px solid var(--color-brand-600) !important; outline-offset: 1px; box-shadow: 0 0 0 4px color-mix(in srgb, var(--color-brand-600) 25%, transparent); }
.syn-wrap.run { background: transparent; }
.syn-wrap.run .frame { box-shadow: none; }
.anim-blink { animation: syn-blink 1s steps(2, start) infinite; }
.anim-pulse { animation: syn-pulse 1.4s ease-in-out infinite; }
.anim-spin { animation: syn-spin 2s linear infinite; }
@keyframes syn-blink { 50% { opacity: 0.15; } }
@keyframes syn-pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.08); } }
@keyframes syn-spin { to { transform: rotate(360deg); } }
.svg { width: 100%; height: 100%; display: block; }
.rect { width: 100%; height: 100%; border-radius: 3px; }
.h { position: absolute; width: 9px; height: 9px; background: var(--color-surface); border: 1.5px solid var(--color-brand-600); border-radius: 2px; z-index: 46; }
.h.nw { left: -5px; top: -5px; cursor: nwse-resize; }
.h.n  { left: calc(50% - 4px); top: -5px; cursor: ns-resize; }
.h.ne { right: -5px; top: -5px; cursor: nesw-resize; }
.h.e  { right: -5px; top: calc(50% - 4px); cursor: ew-resize; }
.h.se { right: -5px; bottom: -5px; cursor: nwse-resize; }
.h.s  { left: calc(50% - 4px); bottom: -5px; cursor: ns-resize; }
.h.sw { left: -5px; bottom: -5px; cursor: nesw-resize; }
.h.w  { left: -5px; top: calc(50% - 4px); cursor: ew-resize; }
.vh { position: absolute; width: 11px; height: 11px; margin: -6px 0 0 -6px; background: var(--color-surface); border: 2px solid var(--color-brand-600); border-radius: 50%; z-index: 47; cursor: grab; box-shadow: 0 1px 2px rgba(0,0,0,.25); }
.vh:hover { background: var(--color-brand-50); }
.rot-badge { position: absolute; left: 50%; top: -46px; background: #111827; color: #fff; font-size: .7rem; font-variant-numeric: tabular-nums; padding: 2px 7px; border-radius: 5px; z-index: 48; pointer-events: none; white-space: nowrap; }
.rot { position: absolute; left: calc(50% - 6px); top: -24px; width: 12px; height: 12px; background: var(--color-brand-600); border: 2px solid var(--color-surface); border-radius: 50%; cursor: grab; z-index: 46; }
.rot::after { content: ''; position: absolute; left: 50%; top: 12px; width: 1px; height: 12px; background: var(--color-brand-600); }
/* ADR 41 F3 — connector point freshness badge, top-right corner of the shape. */
.fresh-badge { position: absolute; top: 2px; right: 2px; z-index: 12; pointer-events: none; font-size: .58rem; line-height: 1; font-weight: 600; padding: 2px 5px; border-radius: 4px; white-space: nowrap; font-variant-numeric: tabular-nums; opacity: .85; }
.fresh-badge.live { background: var(--color-success-50, #e6f4ec); color: var(--color-success-700, #1c7a45); }
.fresh-badge.stored { background: var(--color-surface-2); color: var(--color-muted); }
.fresh-badge.err { background: var(--color-danger-50, #fde8e8); color: var(--color-danger, #c0392b); opacity: 1; }
/* Smart tag — floating pill above the top-left of the selection box. */
.smart-tag { position: absolute; left: 0; top: -26px; display: inline-flex; align-items: center; gap: 3px; height: 20px; padding: 0 7px; background: var(--color-brand-600); color: #fff; border: none; border-radius: 5px; font-size: .66rem; font-weight: 600; cursor: pointer; z-index: 48; box-shadow: 0 1px 3px rgba(0,0,0,.25); white-space: nowrap; }
.smart-tag:hover { background: var(--color-brand-700, var(--color-brand-600)); filter: brightness(1.05); }
</style>
